import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Search, Filter, Globe, User, Sword, ShoppingBag, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const TYPE_CONFIG = {
  universe: { icon: Globe, color: "text-amber-500", bg: "bg-amber-500/10", label: "Universe", linkFn: (item) => `/universe/${item.slug}` },
  character: { icon: User, color: "text-purple-500", bg: "bg-purple-500/10", label: "Character", linkFn: (item) => `/character/${item.id}` },
  weapon: { icon: Sword, color: "text-red-500", bg: "bg-red-500/10", label: "Weapon", linkFn: () => null },
  product: { icon: ShoppingBag, color: "text-emerald-500", bg: "bg-emerald-500/10", label: "Product", linkFn: (item) => `/store/product/${item.id}` },
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeType, setActiveType] = useState(searchParams.get("type") || "");
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "");
  const [universes, setUniverses] = useState([]);

  useEffect(() => {
    axios.get(`${API}/universes`).then(r => setUniverses(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) { setQuery(q); doSearch(q, searchParams.get("type") || "", searchParams.get("category") || ""); }
  }, [searchParams]);

  const doSearch = async (q, type, category) => {
    if (!q?.trim()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: q.trim(), limit: "40" });
      if (type) params.set("type", type);
      if (category) params.set("category", category);
      const res = await axios.get(`${API}/search?${params}`);
      setResults(res.data.results);
      setTotal(res.data.total);
    } catch { setResults([]); }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    const params = new URLSearchParams({ q: query.trim() });
    if (activeType) params.set("type", activeType);
    if (activeCategory) params.set("category", activeCategory);
    setSearchParams(params);
  };

  const setFilter = (type, category) => {
    setActiveType(type);
    setActiveCategory(category);
    if (query.trim()) {
      const params = new URLSearchParams({ q: query.trim() });
      if (type) params.set("type", type);
      if (category) params.set("category", category);
      setSearchParams(params);
    }
  };

  const getImage = (item) => item.cover_image || item.image || item.images?.[0] || null;
  const getUniverseName = (uid) => universes.find(u => u.id === uid)?.name || "";

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-base)]">
      <SEOHead title="Search" description="Search across all FanVerse content" />
      <Navbar />
      <main className="flex-1 pt-20 pb-16 px-4 max-w-5xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/20 bg-amber-500/5 mb-3">
            <Search className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-mono tracking-wider text-amber-500 uppercase">Search Everything</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-4">Advanced Search</h1>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search universes, characters, weapons, products..."
                className="pl-10 input-dark py-3 text-base"
                data-testid="search-page-input"
              />
            </div>
            <Button type="submit" className="btn-primary px-6" data-testid="search-page-submit">
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </form>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="text-slate-500 text-sm flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Type:</span>
          {["", "universe", "character", "weapon", "product"].map(t => (
            <button
              key={t || "all"}
              onClick={() => setFilter(t, activeCategory)}
              className={`px-3 py-1.5 text-xs rounded-lg transition-all capitalize ${
                activeType === t ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-white/5 text-slate-400 border border-white/10 hover:text-white"
              }`}
              data-testid={`filter-type-${t || "all"}`}
            >
              {t || "All"}
            </button>
          ))}
          {activeType === "universe" && (
            <>
              <span className="text-slate-500 text-sm flex items-center gap-1 ml-2">Category:</span>
              {["", "sci-fi", "fantasy", "superhero", "horror"].map(c => (
                <button
                  key={c || "all"}
                  onClick={() => setFilter(activeType, c)}
                  className={`px-3 py-1.5 text-xs rounded-lg transition-all capitalize ${
                    activeCategory === c ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-white/5 text-slate-400 border border-white/10 hover:text-white"
                  }`}
                  data-testid={`filter-cat-${c || "all"}`}
                >
                  {c || "All"}
                </button>
              ))}
            </>
          )}
          {(activeType || activeCategory) && (
            <button onClick={() => setFilter("", "")} className="text-red-400 text-xs hover:text-red-300 flex items-center gap-1">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="spinner"></div></div>
        ) : results.length > 0 ? (
          <>
            <p className="text-slate-400 text-sm mb-4" data-testid="search-result-count">{total} result{total !== 1 ? "s" : ""} for "{searchParams.get("q")}"</p>
            <div className="space-y-3" data-testid="search-results">
              {results.map((item) => {
                const cfg = TYPE_CONFIG[item._type];
                const Icon = cfg.icon;
                const link = cfg.linkFn(item);
                const Wrapper = link ? Link : "div";
                const wrapProps = link ? { to: link } : {};
                return (
                  <Wrapper key={`${item._type}-${item.id}`} {...wrapProps}
                    className="flex items-center gap-4 p-4 rounded-xl bg-[var(--bg-surface)] border border-white/5 hover:border-amber-500/20 transition-all group cursor-pointer"
                    data-testid={`search-result-${item.id}`}
                  >
                    {getImage(item) ? (
                      <img src={getImage(item)} alt={item.name} className="w-14 h-14 rounded-lg object-cover border border-white/10 flex-shrink-0" />
                    ) : (
                      <div className={`w-14 h-14 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${cfg.color}`} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-[10px] font-mono uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
                        {item.universe_id && <span className="text-[10px] text-slate-600">{getUniverseName(item.universe_id)}</span>}
                        {item.category && <span className="text-[10px] text-slate-600 capitalize">{item.category}</span>}
                      </div>
                      <h3 className="text-white font-semibold font-['Outfit'] group-hover:text-amber-400 transition-colors truncate">{item.name}</h3>
                      {item.description && <p className="text-slate-400 text-xs line-clamp-1 mt-0.5">{item.description}</p>}
                    </div>
                    {item.price != null && <span className="text-amber-500 font-bold text-sm">R{item.price}</span>}
                  </Wrapper>
                );
              })}
            </div>
          </>
        ) : searchParams.get("q") ? (
          <div className="text-center py-16 card-surface">
            <Search className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No results found for "{searchParams.get("q")}"</p>
            <p className="text-slate-600 text-sm mt-1">Try different keywords or remove filters</p>
          </div>
        ) : (
          <div className="text-center py-16 card-surface">
            <Search className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Enter a search term to explore FanVerse</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default SearchPage;
